import type { AxiosError, AxiosResponse } from "axios";
import type { AskHelenaVoiceSession } from "shared";
import { axiosClient } from "utilz";
import { apiErrorHandler } from "utilz/helpers";

/**
 * Voice dictation sessions on helena-ask-service. No audio passes through
 * these calls: the browser streams straight to Soniox with the single-use key
 * the session call returns. The service only mints that key, records usage and
 * stores the finished recording.
 *
 * Same convention as the history calls: `axiosClient` with an absolute URL, so
 * the interceptor attaches a fresh token for the audience this service accepts.
 */

const ASK_BASE_URL = import.meta.env.VITE_APP_HELENA_ASK_BASE_URL;

const voiceSessionPath = (sessionId: string) =>
  `${ASK_BASE_URL}/api/v1/voice/sessions/${encodeURIComponent(sessionId)}`;

/**
 * 403 `dictation_disabled`, 429 `dictation_minutes_exhausted` (+ `resetsAt`)
 * and 502 `dictation_unavailable` arrive as ordinary API errors; the caller
 * reads `status` and the raw body off the thrown `APIRequestError`.
 */
export const startAskHelenaVoiceSession = async ({
  language,
}: {
  language: string;
}): Promise<AskHelenaVoiceSession> => {
  try {
    const response = await axiosClient.post(
      `${ASK_BASE_URL}/api/v1/voice/sessions`,
      { language },
    );
    const resData = response.data;
    if (resData.success) {
      return resData?.data;
    } else {
      throw response;
    }
  } catch (error: unknown) {
    return apiErrorHandler(error as AxiosError | AxiosResponse);
  }
};

/** Always sent once per minted session — also on discard and on failure. */
export const endAskHelenaVoiceSession = async ({
  sessionId,
  audioSeconds,
}: {
  sessionId: string;
  audioSeconds: number;
}): Promise<void> => {
  try {
    const response = await axiosClient.post(
      `${voiceSessionPath(sessionId)}/end`,
      { audioSeconds },
    );
    if (!response.data?.success) throw response;
  } catch (error: unknown) {
    return apiErrorHandler(error as AxiosError | AxiosResponse);
  }
};

/**
 * Deliberate divergence from the api-layer convention, like `/ask`: this is the
 * one call that has to survive the tab closing, and only `fetch` with
 * `keepalive` does. axios has no equivalent. Fire-and-forget — there is no page
 * left to report a failure to.
 */
export const endAskHelenaVoiceSessionOnUnload = ({
  sessionId,
  audioSeconds,
  accessToken,
}: {
  sessionId: string;
  audioSeconds: number;
  accessToken: string;
}): void => {
  void fetch(`${voiceSessionPath(sessionId)}/end`, {
    method: "POST",
    keepalive: true,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify({ audioSeconds }),
  }).catch(() => undefined);
};

/** Multipart field `file`, at most 10 MB. 409 means it was already uploaded. */
export const uploadAskHelenaVoiceAudio = async ({
  sessionId,
  recording,
}: {
  sessionId: string;
  recording: Blob;
}): Promise<void> => {
  const extension = recording.type.includes("mp4") ? "m4a" : "webm";
  const formData = new FormData();
  formData.append("file", recording, `dictation.${extension}`);

  try {
    const response = await axiosClient.post(
      `${voiceSessionPath(sessionId)}/audio`,
      formData,
    );
    if (!response.data?.success) throw response;
  } catch (error: unknown) {
    return apiErrorHandler(error as AxiosError | AxiosResponse);
  }
};
